/**
 * @format
 */

import React from 'react';
import { Linking, StyleSheet, Text, View } from 'react-native';
import { Icon, ListItem } from 'react-native-elements';
import lodash, { isArray } from 'lodash';
import MapView, { Marker } from 'react-native-maps';

const debug = require('debug')('se:advertitem:advertContactList');

import HourList from '../../components/hourlist';
import { Advertisement, BusinessHour } from '../../../api/adserver';
import config from '../../lib/config';
import { openLocation, withHttp } from '../../lib/linking';
import ZTMarker from './Marker';

export interface Props {
  advert: Advertisement;
}

interface RenderItemProps {
  key: string;
  text: string;
  icon: string;
  action: any;
  advert: Advertisement;
}

interface ListParamsProps {
  itemParams: {
    bottomDivider: boolean;
  };
  onPress?: (item: Advertisement) => void;
}

const DefaultListParams: ListParamsProps = {
  itemParams: {
    bottomDivider: true,
  },
};

enum ContactItemType {
  Directions = 'directions',
  Email = 'email',
  Website = 'website',
  Call = 'call',
}

const onPressContact = (
  itemType: string,
  action: any,
  advert: Advertisement,
) => {
  debug(
    `onPressContact action type: ${itemType}, action: ${JSON.stringify(
      action,
    )}`,
  );
  switch (itemType) {
    case ContactItemType.Email: {
      const { email } = action;
      if (!email) {
        debug('email not set.  not linking');
        break;
      }
      Linking.openURL(`mailto:${email}`)
        .then(() => debug('opened email'))
        .catch((e) => debug(`error opening email.  ${e.toString()}`));
      break;
    }
    case ContactItemType.Call: {
      const { phone } = action;
      if (!phone) {
        debug('call not set.  not linking');
        break;
      }
      Linking.openURL(`tel:${phone}`)
        .then(() => debug('opened call'))
        .catch((e) => debug(`error opening call.  ${e.toString()}`));
      break;
    }
    case ContactItemType.Website: {
      const { url } = action;
      if (!url) {
        debug('url not set.  not linking');
        break;
      }
      const linkUrl = withHttp(url);
      Linking.openURL(linkUrl)
        .then(() => debug(`opened url:${linkUrl}`))
        .catch((e) => debug(`error opening url.  ${e.toString()}`));
      break;
    }
    case ContactItemType.Directions: {
      const { latitude, longitude } = action;
      if (!latitude || !longitude) {
        debug('lat/lng not set.  not opening directions');
        break;
      }
      openLocation(latitude, longitude, advert);
      break;
    }
    default: {
      debug(
        `unknown onPressContact itemType: ${itemType}, action: ${JSON.stringify(
          action,
        )}, advert.id: ${advert.id}.  Doing nothing`,
      );
    }
  }
};

const renderItem = (
  { icon, key, text, advert, action }: RenderItemProps,
  { itemParams, onPress }: ListParamsProps = DefaultListParams,
) => {
  return (
    <ListItem
      tvParallaxProperties={null}
      hasTVPreferredFocus={false}
      key={key}
      {...itemParams}
      onPress={() => {
        if (onPress) {
          onPress(advert);
        } else {
          onPressContact(key, action, advert);
        }
      }}
    >
      <Icon name={icon} size={14} tvParallaxProperties={null} />
      <ListItem.Content>
        <Text style={[styles.detailText, styles.subSectionBody]}>{text}</Text>
      </ListItem.Content>
      <ListItem.Chevron tvParallaxProperties={null} />
    </ListItem>
  );
};

function hasAction(title: string, advert: Advertisement) {
  if (!advert.actions || advert.actions.length === 0) {
    return false;
  }
  const action = advert.actions.find(
    (a) => a.title.toLowerCase() === title.toLowerCase(),
  );
  return action;
}

const renderDirections = (advert: Advertisement) => {
  const { closest_location } = advert;
  if (
    !closest_location ||
    !closest_location.latitude ||
    !closest_location.longitude
  ) {
    return null;
  }
  const { longitude, latitude } = closest_location;
  return renderItem({
    advert,
    icon: 'directions',
    key: ContactItemType.Directions,
    text: 'Get Directions',
    action: {
      longitude,
      latitude,
    },
  });
};

const renderEmail = (advert: Advertisement) => {
  const { closest_location } = advert;
  let contact_email;
  if (!closest_location || !closest_location.contact_email) {
    // try to infer from actions
    const webAction = hasAction('email', advert);
    if (!webAction) {
      return null;
    }
    contact_email = webAction.url;
  } else {
    contact_email = closest_location.contact_email;
  }

  return renderItem({
    advert,
    icon: 'email',
    key: ContactItemType.Email,
    text: 'Email',
    action: {
      email: contact_email,
    },
  });
};

const renderWebsite = (advert: Advertisement, params: ListParamsProps) => {
  const { closest_location } = advert;
  let contact_website;
  if (!closest_location || !closest_location.contact_website) {
    // try to infer from actions
    const webAction = hasAction('web', advert);
    if (!webAction) {
      return null;
    }
    contact_website = webAction.url;
  } else {
    contact_website = closest_location.contact_website;
  }
  return renderItem(
    {
      advert,
      icon: 'web',
      key: ContactItemType.Website,
      text: 'Website',
      action: {
        url: contact_website,
      },
    },
    params,
  );
};

const renderCall = (advert: Advertisement) => {
  const { closest_location } = advert;
  let contact_phone;
  if (!closest_location || !closest_location.contact_phone) {
    // try to infer from actions
    const webAction = hasAction('call', advert);
    if (!webAction) {
      return null;
    }
    contact_phone = webAction.url;
  } else {
    contact_phone = closest_location.contact_phone;
  }

  return renderItem({
    advert,
    icon: 'phone',
    key: ContactItemType.Call,
    text: 'Call',
    action: {
      phone: contact_phone,
    },
  });
};

const renderHours = (advert: Advertisement, hours: BusinessHour[] | null) => {
  if (!hours || !isArray(hours) || hours.length === 0) {
    return null;
  }
  return (
    <>
      <ListItem
        key={'hours'}
        tvParallaxProperties={null}
        hasTVPreferredFocus={false}
      >
        <Icon name={'watch'} size={14} tvParallaxProperties={null} />
        <ListItem.Content>
          <Text style={[styles.detailText, styles.subSectionBody]}>Hours</Text>
        </ListItem.Content>
      </ListItem>
      <View style={styles.hoursContainer}>
        <HourList list={hours} />
      </View>
    </>
  );
};

const renderMap = (advert: Advertisement) => {
  const { closest_location, show_map } = advert;
  if (!show_map || !closest_location) {
    return null;
  }
  const { longitude, latitude } = closest_location;
  if (!longitude || !latitude) {
    return null;
  }
  return (
    <MapView
      initialRegion={{
        latitude,
        longitude,
        latitudeDelta: 0.0922,
        longitudeDelta: 0.0421,
      }}
      style={styles.mapView}
    >
      <ZTMarker
        key={'location'}
        coordinate={{
          latitude,
          longitude,
        }}
        title={closest_location.name}
      />
    </MapView>
  );
};

const AdvertContactList: React.FC<Props> = ({ advert }) => {
  const { closest_location, business_hours } = advert;
  let hours;
  let hasHours = lodash.isArray(business_hours) && business_hours.length > 0;
  if (!hasHours) {
    hours = lodash.get(closest_location, 'business_hours', null);
    if (hours && lodash.isArray(hours)) {
      hasHours = hours.length > 0;
    }
  } else {
    hours = advert.business_hours;
  }

  return (
    <View>
      {renderMap(advert)}
      {renderDirections(advert)}
      {renderEmail(advert)}
      {renderCall(advert)}
      {renderWebsite(advert, {
        itemParams: {
          bottomDivider: hasHours,
        },
      })}
      {hasHours && renderHours(advert, hours)}
    </View>
  );
};

const styles = StyleSheet.create({
  detailText: {
    fontSize: 14,
  },
  subSectionBody: {
    color: config.theme.advert.subSectionHeadingTextColor,
  },
  hoursContainer: {
    marginStart: 50,
    marginEnd: 30,
  },
  mapView: {
    width: 'auto',
    height: 180,
    marginBottom: 33,
  },
});

export default AdvertContactList;
